import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ChevronDown, ChevronUp, History, Users, Building2, Mail,
  CheckCircle2, XCircle
} from 'lucide-react';

function timeAgo(dateStr) {
  const now = new Date();
  const d = new Date(dateStr);
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function BlastHistorySection({ leadId, refreshKey }) {
  const [blasts, setBlasts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [expandedBlast, setExpandedBlast] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await API.get(`/leads/${leadId}/blast-history`);
      setBlasts(res.data);
    } catch {}
  }, [leadId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory, refreshKey]);

  if (blasts.length === 0 && !expanded) return null;

  const toggleBlast = (id) => setExpandedBlast(prev => prev === id ? null : id);

  return (
    <Card className="bg-white border-gray-200 shadow-sm" data-testid="blast-history-section">
      <CardContent className="p-0">
        <button
          className="w-full flex items-center justify-between p-5 text-left"
          onClick={() => setExpanded(!expanded)}
          data-testid="toggle-blast-history"
        >
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-amber-50 flex items-center justify-center">
              <History className="h-4.5 w-4.5 text-amber-600" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Blast History</p>
              <p className="text-xs text-gray-500">
                {blasts.length} blast{blasts.length !== 1 ? 's' : ''} sent
              </p>
            </div>
          </div>
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>

        {expanded && (
          <div className="px-5 pb-5 pt-0 space-y-2 border-t border-gray-100">
            {blasts.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No blasts sent yet for this property.</p>
            ) : blasts.map(blast => {
              const isBuyers = blast.recipient_type === 'buyers';
              const Icon = isBuyers ? Users : Building2;
              const isOpen = expandedBlast === blast.id;

              return (
                <div
                  key={blast.id}
                  data-testid={`blast-record-${blast.id}`}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                    onClick={() => toggleBlast(blast.id)}
                    data-testid={`toggle-blast-detail-${blast.id}`}
                  >
                    <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${
                      isBuyers ? 'bg-blue-50' : 'bg-purple-50'
                    }`}>
                      <Icon className={`h-4 w-4 ${isBuyers ? 'text-blue-600' : 'text-purple-600'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{blast.subject}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-500">{timeAgo(blast.sent_at)}</span>
                        <span className="text-gray-300">·</span>
                        <span className="text-xs text-gray-500 capitalize">{blast.recipient_type}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge
                        variant="outline"
                        className={`text-[10px] font-medium ${
                          blast.failed_count > 0
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        }`}
                      >
                        {blast.sent_count}/{blast.total_recipients} sent
                      </Badge>
                      {isOpen
                        ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
                        : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                      }
                    </div>
                  </button>

                  {isOpen && blast.recipients && (
                    <div className="border-t border-gray-100 px-4 py-3 bg-gray-50/50 space-y-1.5">
                      {blast.recipients.map((r, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs" data-testid={`blast-recipient-${i}`}>
                          {r.status === 'sent' ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                          )}
                          <Mail className="h-3 w-3 text-gray-400 shrink-0" />
                          <span className="text-gray-700 truncate">{r.email}</span>
                          {r.sent_via && (
                            <span className="text-gray-400 ml-auto shrink-0">via {r.sent_via}</span>
                          )}
                        </div>
                      ))}
                      <p className="text-[10px] text-gray-400 pt-1">
                        {new Date(blast.sent_at).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                          hour: 'numeric', minute: '2-digit', hour12: true
                        })}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
